/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from "react"

import { AnimatePresence, motion } from "motion/react"
import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ChatFooterProps } from "./chat-footer-types"
import type { RealtimeAudioError } from "../hooks/use-realtime-audio"
import type { GPTModelItem } from "@/api/models"

import { AudioLines, Edit3, ListPlus, Orbit, Plus, Send, Square } from "@/components/icons"
import { UsageIndicator } from "@/features/fair-use/components/usage-indicator"
import { useFairUseStore } from "@/features/fair-use/fair-use-store"
import {
  isFairUseSendBlocked,
  useMyUsageSnapshot,
} from "@/features/fair-use/hooks/use-my-usage-snapshot"
import { useIsPlanFeatureLocked } from "@/features/fair-use/hooks/use-plan-entitlements"
import { ProjectEmbeddingIndicator } from "@/features/projects/components/project-embedding-indicator"
import { ProjectSettingsDialog } from "@/features/projects/components/project-settings-dialog"
import { useProject, useUploadProjectFiles } from "@/hooks/use-projects"
import { useSessionId } from "@/hooks/use-session-id"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { getInjectedLinks } from "@/lib/runtime-branding"
import { cn } from "@/lib/utils"
import TokenService from "@/services/token-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { ChatFooterAskAboutPill } from "./chat-footer-ask-about-pill"
import { ChatFooterFileList } from "./chat-footer-file-list"
import { isRedirectOverTokenLimit } from "./chat-footer-redirect-utils"
import { FilePickerDialog, type FilePickerSource } from "./file-picker-dialog"
import { ReasoningModePill } from "./reasoning-mode-pill"
import { SlashCommandMenu } from "./slash-command-menu"
import { getFilteredCommands } from "./slash-command-menu-utils"
import {
  findSlashCommand,
  SLASH_COMMAND_COMMITTED_REGEX,
  SLASH_COMMAND_PARTIAL_REGEX,
  type SlashCommand,
  type SlashCommandName,
} from "./slash-commands"
import { SteerNoteChips } from "./steer-note-chips"
import { VoiceInput } from "./voice-input"
import { buttonVariants } from "../../../components/ui/button-variants"
import { Textarea } from "../../../components/ui/textarea"
import { Toggle } from "../../../components/ui/toggle"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip"
import { useChatFooter } from "../hooks/use-chat-footer"
import { useModelToggleShortcuts } from "../hooks/use-model-toggle-shortcuts"
import { registerSteerComposer } from "../hooks/use-steer-actions"
import { useVoiceEngine } from "../hooks/use-voice-engine"
import { useVoiceModeHandlers } from "../hooks/use-voice-mode-handlers"
import { type ChatUploadSizeError, getChatUploadSizeError } from "../utils/chat-upload-size"

export type { ChatFooterProps, SharedChatProps } from "./chat-footer-types"

const CHAT_TEXTAREA_INITIAL_HEIGHT = 16 // Initial height in pixels

// Touch targets. DESIGN.md sets a 40 px minimum; the composer draws its
// buttons at 28 px (Claude-composer proportions, owner sign-off on #3122) and
// keeps the floor with an invisible inset pseudo-element:
// - in the input box the buttons reach 6 px on every side (40 px); the voice
//   trigger reaches 8 px (44 px, pinned by chat-footer-cancel-option.test);
// - in the toolbar, where the attach button sits 4 px from the tools trigger,
//   the reach is 4 px sideways and 6 px vertically (36 x 40 px), so
//   neighbouring targets touch but never overlap and steal each other's taps.
const HIT40 = "relative before:absolute before:-inset-1.5 before:content-['']"
const TOOLBAR_HIT =
  "relative before:absolute before:-inset-x-1 before:-inset-y-1.5 before:content-['']"

// React replays events from portalled descendants (the model picker's popover,
// tooltips, menus) through the React tree, so a click inside one of those
// panels also reaches the composer box handlers below. Acting on it there would
// cancel the click's default action — killing radio/label activation in the
// model picker — and pull focus back to the textarea, which Radix reads as
// focus-outside and closes the panel. Only handle clicks that really happened
// inside the composer's own DOM subtree.
function isEventFromComposer(event: React.MouseEvent<HTMLElement>): boolean {
  return event.currentTarget.contains(event.target as Node | null)
}

// Stable fallback so surfaces without reasoning models don't re-attach the
// shortcut listener every render (a fresh [] would churn the effect deps).
const NO_REASONING_MODELS: GPTModelItem[] = []
export function ChatFooter({
  children,
  attachTitle,
  composerBar,
  focusOnMount,
  onCancel,
  onSend,
  totalTokenCount,
  showCancelOption,
  cancelAvailable = true,
  isUploading,
  placeholder,
  disableSend,
  clearOnSend,
  initQuestion,
  onQueue,
  availableFiles,
  filesInIndex,
  sessionFileRefs,
  removingFileName,
  disableFileSelection,
  onFilesAdded,
  onRemoveFile,
  compactFileList,
  onOpenSelectedFilesContext,
  resetFiles,
  maxTokens,
  dropzoneOptions,
  maxFiles = 10,
  textareaDisabled,
  showChatOptions,
  customSendQuestiondDisabledLabel,
  customSendQuestionLabel,
  changeTokenCounts,
  showHighReasoning,
  enableHighReasoning,
  onHighReasoningChange,
  showReasoningMode,
  reasoningMode,
  onReasoningModeChange,
  modelOverride,
  onModelOverrideChange,
  effortOverride,
  onEffortOverrideChange,
  availableReasoningModels,
  selectedReasoningModel,
  actionButtons,
  onSetQuestion,
  onGraphFileSelected,
  onDocumentsSelected,
  onRemoveDocument,
  selectedDocuments,
  isNavigator = false,
  defaultFilePickerSource,
  filePickerAccept,
  filePickerInfoMessage,
  onFilePickerFilesRejected,
  allowFileOnlySend,
  projectId,
  sessionType,
  isReader = false,
  onVoiceMessage,
  onStreamingVoiceMessage,
  onStreamingComplete,
  onGetSendSessionContext,
  onBeforeVoiceModeStart,
  voiceSessionRoutePrefix = "/explorer/chat",
  onGetToggleVoiceMode,
  hideVoiceMode,
  hideVoiceModeButton,
  hideVoiceInputButton,
  askAboutText,
  onClearAskAboutText,
  onFirstUserInput,
  onSlashCommandDetected,
}: ChatFooterProps) {
  const { t } = useTranslation()
  const currentSessionId = useSessionId()
  const isCanvasOpen = useContentSidebarStore(
    (state) => state.isOpen && state.currentPanel?.id === "canvas"
  )
  const isCanvasFocused = useContentSidebarStore(
    (state) => state.isFocused && state.currentPanel?.id === "canvas"
  )
  const requestCanvasClose = useContentSidebarStore((state) => state.requestClose)
  const isCompactCanvasLayout = useViewportBelow({ breakpoint: 1024 })
  const footerRef = useRef<HTMLElement | null>(null)
  const [filePickerOpen, setFilePickerOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [filePickerSource, setFilePickerSource] = useState<FilePickerSource>(
    defaultFilePickerSource ?? (isNavigator ? "local" : "recent")
  )
  const [isUploadMenuOpen] = useState(false)
  const [movingFileName, setMovingFileName] = useState<string | null>(null)
  // Autocomplete state: only opens when the user has just typed `/` at the
  // start of an otherwise-empty-of-committed-command input.
  const [slashMenuOpen, setSlashMenuOpen] = useState(false)
  const [slashFilter, setSlashFilter] = useState("")
  const [slashIndex, setSlashIndex] = useState(0)
  // Fire `onFirstUserInput` only once per mount. Without this, every
  // empty→non-empty transition of the textarea triggers it — and
  // `commitSlashCommand` resets the textarea to "" each time the user picks
  // `/deep`, `/fast`, etc. So a user typing `/deep`, `/fast`, then a real
  // query triggered three optimistic session creates instead of one.
  const firstUserInputFiredRef = useRef(false)

  // Alt+0–6 / Alt+± model-and-effort shortcuts, active exactly when the
  // reasoning pill below renders enabled (same render/disabled conditions).
  // Single source of truth for the hook AND the pill's kbd hints, so a surface
  // can never advertise shortcuts it doesn't install: `onModelOverrideChange`
  // is required because the hook no-ops without it (Dash omits it), and reader
  // mode early-returns before the composer so shortcuts must be off there too.
  const modelShortcutsEnabled =
    Boolean(showReasoningMode && reasoningMode && onReasoningModeChange && onModelOverrideChange) &&
    !showCancelOption &&
    !isReader
  useModelToggleShortcuts({
    enabled: modelShortcutsEnabled,
    availableModels: availableReasoningModels ?? NO_REASONING_MODELS,
    selectedModel: selectedReasoningModel,
    mode: reasoningMode,
    effortOverride,
    onModelOverrideChange,
    onEffortOverrideChange,
  })

  const { data: project } = useProject(projectId, sessionType)
  const uploadProjectFiles = useUploadProjectFiles()
  const handleMoveToProject = useCallback(
    async (file: File) => {
      if (!projectId) return
      setMovingFileName(file.name)
      try {
        await uploadProjectFiles.mutateAsync({ projectId, files: [file] })
        onRemoveFile?.(file)
      } finally {
        setMovingFileName(null)
      }
    },
    [projectId, uploadProjectFiles, onRemoveFile]
  )
  useEffect(
    () => setFilePickerSource(defaultFilePickerSource ?? (isNavigator ? "local" : "recent")),
    [isNavigator, defaultFilePickerSource]
  )

  useEffect(() => {
    const footerElement = footerRef.current
    if (!footerElement) return

    const parentElement = footerElement.parentElement
    if (!parentElement) return

    const updateFooterHeight = () => {
      const height = footerElement.getBoundingClientRect().height
      parentElement.style.setProperty("--chat-footer-height", `${Math.ceil(height)}px`)
    }

    updateFooterHeight()

    if (typeof ResizeObserver === "undefined") {
      return () => {
        parentElement.style.removeProperty("--chat-footer-height")
      }
    }

    const observer = new ResizeObserver(() => updateFooterHeight())
    observer.observe(footerElement)

    return () => {
      observer.disconnect()
      parentElement.style.removeProperty("--chat-footer-height")
    }
  }, [])

  const showChatUploadSizeError = useCallback(
    (sizeError: ChatUploadSizeError) => {
      if (sizeError.reason === "single-file") {
        toast.error(
          t("questioninput.chat_upload_file_too_large", {
            fileName: sizeError.fileName,
            fileSize: sizeError.fileSizeLabel,
            maxSize: sizeError.maxSizeLabel,
          })
        )
        return
      }

      toast.error(
        t("questioninput.chat_upload_too_large", {
          totalSize: sizeError.totalSizeLabel,
          maxSize: sizeError.maxSizeLabel,
        })
      )
    },
    [t]
  )

  const tryAddFiles = useCallback(
    (incomingFiles: File[]) => {
      if (disableFileSelection) return false
      if (incomingFiles.length === 0) return true

      const nextFiles = [...(availableFiles ?? []), ...incomingFiles]

      if (nextFiles.length > maxFiles) {
        toast.error(t("questioninput.max_files_reached"))
        return false
      }

      const sizeError = getChatUploadSizeError(nextFiles)
      if (sizeError) {
        showChatUploadSizeError(sizeError)
        return false
      }

      onFilesAdded?.(incomingFiles)
      return true
    },
    [availableFiles, disableFileSelection, maxFiles, onFilesAdded, showChatUploadSizeError, t]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      tryAddFiles(acceptedFiles)
    },
    [tryAddFiles]
  )

  const { rootRef, getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disableFileSelection,
    ...dropzoneOptions,
  })

  const handleOpenCanvas = useCallback(() => {
    if (!isCanvasOpen) return
    useContentSidebarStore.setState({ isFocused: true })
  }, [isCanvasOpen])

  const handleCloseCanvas = useCallback(() => {
    void requestCanvasClose()
  }, [requestCanvasClose])

  // Shared send/queue preprocessing: upload size guard, reasoning-slash strip,
  // and Ask-about prefix. Queue must use the same path so enqueued text matches
  // what a normal send would have produced (#2437 review).
  const prepareQuestionForSend = useCallback(
    (question: string): string | false => {
      if (availableFiles?.length) {
        const sizeError = getChatUploadSizeError(availableFiles)
        if (sizeError) {
          showChatUploadSizeError(sizeError)
          return false
        }
      }

      // Defensive strip: if a reasoning prefix survived all the way to send
      // (e.g. the user pasted it in), remove it — the mode has already been
      // applied to the store via onSlashCommandDetected.
      const finalQuestion = question.replace(/^\s*\/(auto|fast|thinking|bigbrain)\s+/i, "")
      if (askAboutText) {
        const prefix = t("questioninput.ask_about_prefix")
        return finalQuestion.trim()
          ? `${prefix} ${askAboutText}\n\n${finalQuestion}`
          : `${prefix} ${askAboutText}`
      }
      return finalQuestion
    },
    [askAboutText, availableFiles, showChatUploadSizeError, t]
  )

  // Fair use (#2625): a hard-blocked user must not be able to fire a turn the
  // backend will refuse with a 429 anyway. Gated at `wrappedOnSend` AND
  // `wrappedOnQueue` — the canonical send/queue entries — rather than only on
  // the button, because the button is not the only way to send: Enter goes
  // through `useChatFooter`'s `sendQuestion`, and Enter-while-busy / Explorer
  // `onQueue` would otherwise enqueue a message that can never be sent.
  // Deliberately NOT routed through `disableSend`, which means "a turn is
  // running" to `useChatFooter` and would turn Send into Queue (`canQueue`).
  // Returning `false` here also preserves the draft: `sendQuestion` only
  // clears the textarea once `onSend`/`onQueue` has accepted.
  const fairUseUsage = useMyUsageSnapshot()
  const fairUseSendBlocked = isFairUseSendBlocked(fairUseUsage)
  const openFairUsePopover = useFairUseStore((state) => state.openPopover)
  const openFairUseRequestDialog = useFairUseStore((state) => state.openRequestDialog)

  const wrappedOnSend = useCallback(
    (question: string) => {
      if (fairUseSendBlocked) {
        // Never a silent drop: surface the budget popover (with its own
        // "Request more" CTA) so the refusal is explained where the user just
        // clicked.
        openFairUsePopover()
        return false
      }
      const prepared = prepareQuestionForSend(question)
      if (prepared === false) return false
      const sendResult = onSend(prepared)
      if (sendResult === false) return false
      if (askAboutText) onClearAskAboutText?.()
      return sendResult
    },
    [
      askAboutText,
      fairUseSendBlocked,
      onClearAskAboutText,
      onSend,
      openFairUsePopover,
      prepareQuestionForSend,
    ]
  )

  const wrappedOnQueue = useCallback(
    (question: string) => {
      if (fairUseSendBlocked) {
        openFairUsePopover()
        return false
      }
      if (!onQueue) return
      const prepared = prepareQuestionForSend(question)
      if (prepared === false) return false
      const queueResult = onQueue(prepared)
      if (queueResult === false) return false
      if (askAboutText) onClearAskAboutText?.()
    },
    [
      askAboutText,
      fairUseSendBlocked,
      onClearAskAboutText,
      onQueue,
      openFairUsePopover,
      prepareQuestionForSend,
    ]
  )

  const {
    handleTextAreaClick,
    handleKeyDown,
    textareaRef,
    handleFilePaste,
    setQuestion,
    question,
    adjustTextareaHeight,
    sendQuestion,
    sendQuestionDisabled,
    canQueue,
    isTextareaFocused,
    shouldBlockSending,
    getReportedTextTokenCount,
  } = useChatFooter({
    chatboxRef: rootRef,
    onFilesAdded: tryAddFiles,
    disableSend,
    initQuestion,
    onSend: wrappedOnSend,
    onQueue: onQueue ? wrappedOnQueue : undefined,
    totalTokenCount,
    clearOnSend,
    maxTokens,
    resetFiles,
    disableFileSelection,
    acceptedFileTypes: dropzoneOptions?.accept,
    availableFiles,
    changeTokenCounts,
    allowFileOnlySend,
    hasExternalContent: !!askAboutText,
  })

  // Steer mode (issue #2438): the host page relaxes `disableSend` while a
  // steerable turn is running, a combination that never occurs otherwise
  // (running always implied disableSend before). In that state the Send
  // button renders alongside Stop and sends steering notes into the running
  // turn instead of a new message.
  const steerSendAvailable = showCancelOption && !disableSend

  // Composer bridge for stop-and-redirect: expose read/clear/restore access to
  // the draft plus this footer's canonical send entry so the steer cancel path
  // can stash the draft as pendingRedirect and the redirect auto-send takes
  // the same path as a user click — token-limit gate and prefix handling
  // included (ChatFooterProps.onCancel carries no arguments). The ref keeps
  // the getter stable across keystrokes.
  const questionRef = useRef(question)
  const clearedRedirectBaseTokenCountRef = useRef<number | null>(null)
  useEffect(() => {
    questionRef.current = question
  }, [question])
  const sendRedirectText = useCallback(
    (text: string) => {
      const tokenService = TokenService.getInstance()
      const redirectTokenCount = tokenService.countTokens(text)
      const isOverLimit = isRedirectOverTokenLimit({
        maxTokens,
        totalTokenCount,
        reportedComposerTokenCount: getReportedTextTokenCount(),
        clearedRedirectBaseTokenCount: clearedRedirectBaseTokenCountRef.current,
        hasLiveComposerText: Boolean(questionRef.current),
        redirectTokenCount,
      })
      clearedRedirectBaseTokenCountRef.current = null
      if (isOverLimit) {
        return false
      }
      return wrappedOnSend(text)
    },
    [getReportedTextTokenCount, maxTokens, totalTokenCount, wrappedOnSend]
  )
  useEffect(() => {
    return registerSteerComposer({
      getText: () => questionRef.current,
      clearText: () => {
        clearedRedirectBaseTokenCountRef.current = Math.max(
          0,
          totalTokenCount - getReportedTextTokenCount()
        )
        setQuestion("")
      },
      setText: (text: string) => {
        clearedRedirectBaseTokenCountRef.current = null
        setQuestion(text)
      },
      send: sendRedirectText,
    })
  }, [setQuestion, sendRedirectText])

  // The prop is named ``focusOnMount`` but the effect re-runs every time
  // a dep changes — and ``textareaDisabled`` toggles during a turn (it
  // flips while the agent is running and flips back when the run ends).
  // Without the ref guard below, that means the composer steals focus
  // mid-conversation every time a turn completes. The ref makes the
  // first eligible firing a one-shot: we focus once when the conditions
  // are first met (mount or first-time enable post-mount) and never
  // again for this mount. Bot review on PR #1964.
  const focusedOnceRef = useRef(false)
  useEffect(() => {
    if (focusedOnceRef.current) return
    if (!focusOnMount || textareaDisabled || isReader) return

    const frameId = requestAnimationFrame(() => {
      textareaRef.current?.focus({ preventScroll: true })
      focusedOnceRef.current = true
    })

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [focusOnMount, isReader, textareaDisabled, textareaRef])

  const isVoiceModeEnabled = useVoiceEngine() !== null
  const toggleVoiceModeRef = useRef<(() => void) | null>(null)
  // Fair use (#3087): a plan without `voice` gets the composer of a tenant
  // without voice — the send button stays in the action slot. Round 2 showed a
  // locked microphone there instead, but a user who has never had voice does
  // not know the slot could be a microphone; they only see "send" locked. The
  // plan popover's "Not included in your plan" list is where voice is named,
  // and `voice_fair_use_preflight` still refuses the realtime socket
  // server-side, so this is presentation over an enforcement that exists.
  const isVoicePlanLocked = useIsPlanFeatureLocked("voice")
  const shouldShowVoiceModeButton =
    !showCancelOption &&
    isVoiceModeEnabled &&
    !isVoicePlanLocked &&
    !hideVoiceMode &&
    !hideVoiceModeButton &&
    !question.trim() &&
    !availableFiles?.length &&
    !askAboutText

  let sendQuestionTooltipLabel: string
  if (fairUseSendBlocked) {
    sendQuestionTooltipLabel = t("fairUse.chat.sendBlocked")
  } else if (canQueue) {
    sendQuestionTooltipLabel = t("questioninput.queue_question")
  } else if (disableSend) {
    // externally disabled
    sendQuestionTooltipLabel =
      customSendQuestiondDisabledLabel || t("questioninput.send_question_disabled")
  } else {
    sendQuestionTooltipLabel = customSendQuestionLabel || t("questioninput.send_question")
  }

  // Wrapper to check consent before starting voice mode
  const handleVoiceModeToggle = useCallback(() => {
    // Check if we should proceed with voice mode
    if (onBeforeVoiceModeStart && !onBeforeVoiceModeStart()) {
      return // Consent check failed or modal shown
    }
    toggleVoiceModeRef.current?.()
  }, [onBeforeVoiceModeStart])

  // Surface fatal voice mode errors (mic permission denied, WS connect failure,
  // backend session error frames) to the user instead of failing
  // silently. Without this, callbacks?.onError?.() in useRealtimeAudio is a
  // no-op and the user gets no feedback when something goes wrong.
  const handleVoiceError = useCallback(
    (error: RealtimeAudioError) => {
      toast.error(t(`voice_mode.errors.${error}`))
    },
    [t]
  )

  // Voice mode handlers
  const {
    handleVoiceFairUseRefusal,
    handleVoiceUserTranscript,
    handleVoiceAssistantTranscriptDelta,
    handleVoiceAssistantTranscript,
    handleVoiceTranscriptionFailure,
    handleVoiceToolStart,
    handleVoiceToolComplete,
    handleVoiceInterrupt,
    handleVoiceSessionCreated,
    handleVoiceModeEnd,
  } = useVoiceModeHandlers({
    currentSessionId,
    onVoiceMessage,
    onStreamingVoiceMessage,
    onStreamingComplete,
    voiceSessionRoutePrefix,
  })

  useEffect(() => {
    if (onSetQuestion) {
      const safeSetQuestion = (text: string) => {
        setQuestion(text || "")
      }
      onSetQuestion(safeSetQuestion)
    }
  }, [onSetQuestion, setQuestion])

  // Commit a slash command: fire the host callback, close the menu, and park
  // the remainder of the input back in the textarea.
  const commitSlashCommand = useCallback(
    (command: SlashCommand, rest = "") => {
      onSlashCommandDetected?.(command.name)
      setQuestion(rest)
      setSlashMenuOpen(false)
      setSlashFilter("")
      setSlashIndex(0)
      // Keep focus on the textarea after a menu pick so the user can keep
      // typing without clicking.
      requestAnimationFrame(() => {
        textareaRef.current?.focus()
        adjustTextareaHeight()
      })
    },
    [onSlashCommandDetected, setQuestion, textareaRef, adjustTextareaHeight]
  )

  // Expose raw toggle function to parent (bypasses consent check for post-consent start)
  useEffect(() => {
    if (onGetToggleVoiceMode) {
      // Provide a function that calls the current toggle ref when invoked
      onGetToggleVoiceMode(() => toggleVoiceModeRef.current?.())
    }
  }, [onGetToggleVoiceMode])

  // Hide footer entirely in reader mode
  if (isReader) {
    return (
      <footer ref={footerRef} className="sticky right-0 bottom-0 z-10 w-full px-2 py-4">
        <section className="flex w-full flex-col items-center gap-2">
          <div className="bg-muted/30 flex w-full max-w-3xl items-center justify-center rounded-lg border border-dashed px-4 py-6">
            <p className="text-muted-foreground text-sm">
              {t("chatsession.reader_mode_message", {
                defaultValue:
                  "You are viewing this chat in read-only mode. You cannot send messages.",
              })}
            </p>
          </div>
        </section>
      </footer>
    )
  }

  return (
    <>
      <footer ref={footerRef} className={cn("sticky right-0 bottom-0 z-10 w-full px-2")}>
        <div className="to-background pointer-events-none absolute inset-x-0 top-0 mx-auto h-8 w-full max-w-[877px] -translate-y-full bg-linear-to-b from-transparent to-99%" />
        <section className="bg-background flex w-full flex-col items-center gap-2">
          {isCanvasOpen && isCompactCanvasLayout && !isCanvasFocused && (
            <div className="border-primary/20 bg-primary/5 flex w-full max-w-3xl items-center justify-between gap-3 rounded-lg border px-3 py-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <Edit3 className="text-primary size-3.5 shrink-0" />
                <span className="text-primary truncate text-sm">
                  {t("canvas.open_in_chat_badge", { defaultValue: "Canvas is open" })}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className={cn(
                    buttonVariants({ size: "sm", variant: "ghost" }),
                    "h-7 shrink-0 text-xs"
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleCloseCanvas()
                  }}
                >
                  {t("canvas.close_canvas", { defaultValue: "Close" })}
                </button>
                <button
                  type="button"
                  className={cn(
                    buttonVariants({ size: "sm", variant: "outline" }),
                    "h-7 shrink-0 text-xs"
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleOpenCanvas()
                  }}
                >
                  {t("canvas.open_canvas", { defaultValue: "Open canvas" })}
                </button>
              </div>
            </div>
          )}
          <SteerNoteChips sessionType={sessionType} />
          <section
            className={cn("flex w-full justify-center", {
              "border-secondary rounded-lg border-2 border-dashed": isDragActive,
            })}
            {...getRootProps()}
            role="presentation"
            onClick={(e) => {
              if (!isEventFromComposer(e)) return
              e.preventDefault()
            }}
          >
            <div className="flex w-full max-w-3xl min-w-0 flex-col">
              <section
                data-testid="chat-dropzone"
                role="presentation"
                className={cn(
                  "bg-chat-box relative flex w-full cursor-text flex-col justify-center rounded-lg border p-2"
                )}
                onClick={(e) => {
                  if (!isEventFromComposer(e)) return
                  handleTextAreaClick()
                }}
              >
                {/* Composer queue bar (#2468): the "attached bar" — queue rows
                  and the brief Sending redirect bridge — rendered as the
                  first thing inside this same bordered box, never a separate
                  floating panel. The negative margins cancel this section's
                  own p-2 so the bar's rows span edge-to-edge.
                  `composerBar` is a *rendered* ReactNode, not a boolean, so a
                  truthiness check here can't tell "renders nothing" apart
                  from "renders a real row" — `<ComposerQueueBar
                  queuedMessages={[]} />` is a non-null element even though it
                  returns null. Instead we rely on the DOM: when the child
                  renders null, this wrapper has zero child nodes, so
                  `empty:hidden` (`display: none`) collapses its
                  border/margins to nothing — collapsing back to today's
                  plain composer, byte-for-byte, whenever `composerBar` is
                  undefined or resolves to no visible content. */}
                <div className="border-border -mx-2 -mt-2 mb-2 border-b empty:hidden">
                  {composerBar}
                </div>
                <input data-testid="upload-file-input" {...getInputProps()} />
                {askAboutText && (
                  <div className="flex flex-wrap gap-2 px-3 pb-2">
                    <ChatFooterAskAboutPill
                      text={askAboutText}
                      onRemove={() => onClearAskAboutText?.()}
                    />
                  </div>
                )}
                <ChatFooterFileList
                  availableFiles={availableFiles}
                  compact={compactFileList}
                  filesInIndex={filesInIndex}
                  isUploading={isUploading}
                  movingFileName={movingFileName}
                  projectId={projectId}
                  removingFileName={removingFileName}
                  selectedDocuments={selectedDocuments}
                  sessionFileRefs={sessionFileRefs}
                  onMoveToProject={handleMoveToProject}
                  onOpenContextPanel={onOpenSelectedFilesContext}
                  onRemoveDocument={onRemoveDocument}
                  onRemoveFile={onRemoveFile}
                />
                {/* Input row: the textarea and the primary action (send /
                  queue / stop / voice) share the bordered box, with the
                  action pinned bottom-right so it stays put as the textarea
                  grows. Everything else lives in the toolbar BELOW the box. */}
                <div className="flex items-end gap-2">
                  <div className="relative flex min-h-7 min-w-0 flex-1 items-center">
                    {slashMenuOpen && (
                      <SlashCommandMenu
                        activeIndex={slashIndex}
                        filter={slashFilter}
                        onHover={setSlashIndex}
                        onSelect={(cmd) => commitSlashCommand(cmd)}
                      />
                    )}
                    <Textarea
                      ref={textareaRef}
                      className="flex w-full resize-none flex-col border-0 p-0 px-2 shadow-none focus-visible:ring-0"
                      data-testid="chat-input"
                      disabled={textareaDisabled}
                      placeholder={placeholder}
                      rows={1}
                      style={{ minHeight: `calc(${CHAT_TEXTAREA_INITIAL_HEIGHT}px)` }} // Ensure minimum height
                      value={isDragActive ? t("questioninput.overlay.drop_to_upload") : question}
                      onPaste={handleFilePaste}
                      onBlur={() => {
                        // Delay close so clicks on menu items (onMouseDown) still
                        // fire. commitSlashCommand refocuses the textarea.
                        setTimeout(() => setSlashMenuOpen(false), 120)
                      }}
                      onChange={(e) => {
                        const newValue = e.target.value
                        // 1. Committed form: `/<cmd> rest` → strip prefix and fire.
                        //    Handles /deep, /plan (mode) and /auto, /fast,
                        //    /thinking, /bigbrain (reasoning).
                        const committed = newValue.match(SLASH_COMMAND_COMMITTED_REGEX)
                        if (committed) {
                          const name = committed[1].toLowerCase() as SlashCommandName
                          const cmd = findSlashCommand(name)
                          if (cmd) {
                            const rest = committed[3]
                            commitSlashCommand(cmd, rest)
                            return
                          }
                        }
                        // 2. Partial form: `/<partial>` with no space yet → open
                        //    or update the autocomplete menu.
                        const partial = newValue.match(SLASH_COMMAND_PARTIAL_REGEX)
                        if (partial) {
                          setSlashMenuOpen(true)
                          setSlashFilter(partial[1])
                          setSlashIndex(0)
                        } else if (slashMenuOpen) {
                          setSlashMenuOpen(false)
                          setSlashFilter("")
                        }
                        // Detect transition from empty to non-empty (session
                        // bootstrap hook). Gated by a ref so it fires at most
                        // once per mount: slash-command commits reset the
                        // textarea to "" via setQuestion, which would otherwise
                        // re-arm this transition on every subsequent keystroke
                        // and spawn a new empty session per slash command.
                        const isFirstKeystroke = question.length === 0 && newValue.length > 0
                        setQuestion(newValue)
                        adjustTextareaHeight()
                        if (isFirstKeystroke && !firstUserInputFiredRef.current) {
                          firstUserInputFiredRef.current = true
                          onFirstUserInput?.()
                        }
                      }}
                      onKeyDown={(e) => {
                        if (slashMenuOpen) {
                          const items = getFilteredCommands(slashFilter)
                          if (e.key === "ArrowDown") {
                            e.preventDefault()
                            setSlashIndex((i) => (items.length ? (i + 1) % items.length : 0))
                            return
                          }
                          if (e.key === "ArrowUp") {
                            e.preventDefault()
                            setSlashIndex((i) =>
                              items.length ? (i - 1 + items.length) % items.length : 0
                            )
                            return
                          }
                          if (e.key === "Enter" || e.key === "Tab") {
                            if (items.length > 0) {
                              e.preventDefault()
                              commitSlashCommand(items[slashIndex])
                              return
                            }
                          }
                          if (e.key === "Escape") {
                            e.preventDefault()
                            setSlashMenuOpen(false)
                            setSlashFilter("")
                            setSlashIndex(0)
                            return
                          }
                        }
                        handleKeyDown(e)
                      }}
                    />
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-3.5"
                    data-testid="composer-primary-action"
                  >
                    {showCancelOption && cancelAvailable && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              buttonVariants({
                                size: "icon",
                                variant: "secondary",
                                // 28 px visual, 40 px touch target (DESIGN.md floor)
                                // via the inset pseudo-element; the 14 px group gap keeps
                                // neighbouring targets from overlapping.
                                className: "size-7 cursor-pointer " + HIT40,
                              })
                            )}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              onCancel()
                            }}
                          >
                            <Square
                              className="text-secondary-foreground size-4"
                              fill="currentColor"
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                          <p>{t("questioninput.cancel_question")}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {/* Queue must stay available beside Stop during a busy turn —
                              otherwise mouse (and most mobile) users have no enqueue
                              affordance while `showCancelOption` hides Send (#2437). */}
                    {showCancelOption && canQueue && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={sendQuestionTooltipLabel}
                            data-testid="send-question-button"
                            type="button"
                            className={cn(
                              buttonVariants({
                                size: "icon",
                                // Primary fill: the composer's one main action reads as
                                // brand blue with charcoal ink in both themes. The old
                                // secondary fill was charcoal-on-charcoal in dark mode.
                                variant: "default",
                                className: "size-7 cursor-pointer " + HIT40,
                              })
                            )}
                            onClick={sendQuestion}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <ListPlus className="size-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                          <p>{sendQuestionTooltipLabel}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {!showCancelOption && shouldShowVoiceModeButton && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={t("questioninput.start_voice_mode")}
                            data-testid="voice-mode-button"
                            type="button"
                            className={cn(
                              buttonVariants({
                                size: "icon",
                                // Same primary fill as the send button it stands in for.
                                variant: "default",
                                className:
                                  // 28 px visual to match the send button, with a 44 px
                                  // touch target kept by the inset pseudo-element (WCAG
                                  // 2.5.8; pinned by chat-footer-cancel-option.test).
                                  "relative size-7 cursor-pointer before:absolute before:-inset-2 before:content-['']",
                              })
                            )}
                            onClick={handleVoiceModeToggle}
                            onMouseDown={(e) => {
                              e.preventDefault()
                            }}
                          >
                            <AudioLines className="size-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                          <p>{t("questioninput.start_voice_mode")}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {((!showCancelOption && !shouldShowVoiceModeButton) || steerSendAvailable) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* `aria-disabled`, not `disabled`, for the
                                    fair-use gate (#2625): a truly disabled
                                    button emits no pointer events, so its
                                    tooltip — the only place that explains WHY
                                    sending is off, and the only route to the
                                    "Request more" CTA — would never open. The
                                    send itself is already blocked upstream in
                                    `wrappedOnSend` / `wrappedOnQueue` (Enter
                                    and Enter-while-busy), so this is
                                    presentation, not enforcement. */}
                          <button
                            aria-disabled={fairUseSendBlocked || undefined}
                            aria-label={sendQuestionTooltipLabel}
                            data-fair-use-blocked={fairUseSendBlocked || undefined}
                            data-testid="send-question-button"
                            type="button"
                            className={cn(
                              buttonVariants({
                                size: "icon",
                                // Primary fill: the composer's one main action reads as
                                // brand blue with charcoal ink in both themes. The old
                                // secondary fill was charcoal-on-charcoal in dark mode.
                                variant: "default",
                                className: "size-7 cursor-pointer " + HIT40,
                              }),
                              fairUseSendBlocked && "cursor-not-allowed opacity-50"
                            )}
                            disabled={
                              !fairUseSendBlocked &&
                              (sendQuestionDisabled || shouldBlockSending) &&
                              !canQueue
                            }
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              if (fairUseSendBlocked) {
                                openFairUsePopover()
                                return
                              }
                              sendQuestion()
                            }}
                          >
                            {canQueue && !fairUseSendBlocked ? (
                              <ListPlus className="size-4" />
                            ) : (
                              <Send className="size-4" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                          <p>{sendQuestionTooltipLabel}</p>
                          {/* The gate's one actionable exit. Rendered
                                    inside the tooltip because that is where
                                    the user lands when they try to send and
                                    nothing happens; Radix keeps the content
                                    hoverable by default, so the button is
                                    reachable. */}
                          {fairUseSendBlocked && (
                            <button
                              className="text-primary mt-1.5 cursor-pointer text-xs font-semibold underline underline-offset-2"
                              data-testid="fair-use-send-blocked-request-more"
                              type="button"
                              onClick={() => openFairUseRequestDialog()}
                            >
                              {t("fairUse.popover.requestMore")}
                            </button>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </section>
              {/* Composer toolbar, rendered under the box (Claude-composer
                layout): attach, modes, model pill and voice on the left,
                project indicator + custom actions on the right, and the
                fair-use plan/budget indicator at the far right. The
                indicator is mounted OUTSIDE the collapsible options block
                because `routeFairUseError` opens its popover imperatively,
                which is a silent no-op when nothing is mounted: surfaces
                that pass `showChatOptions={false}` (e.g. the Studio AI
                image-editor panel) collapse that block entirely and would
                leave a blocked user with no indicator and no way to ask for
                budget. `UsageIndicator` renders null when fair use is
                disabled, and `empty:hidden` then collapses this row so the
                composer stays as before for everyone else. */}
              {/* The transparent side borders mirror the box's own border so
                  the toolbar's content edge sits exactly under the box's
                  content edge, and the padding is tuned so the budget ring's
                  centre sits on the action button's centre line (20 px ring
                  in a 28 px button: 6 px ring padding + 6 px + border puts
                  the ring's right edge 4 px inside the button's) and the
                  attach glyph starts on the prompt text's left edge (28 px
                  button, 16 px glyph: 10 px + 6 px + border). */}
              <section
                className="flex items-center gap-2 border-x border-transparent pt-0.5 pr-1.5 pl-2.5 empty:hidden"
                data-testid="composer-toolbar"
              >
                <AnimatePresence>
                  {(isTextareaFocused || showChatOptions || isUploading || isUploadMenuOpen) && (
                    <motion.div
                      key="chat-options"
                      animate={{ opacity: 1, height: "auto" }}
                      // overflow-hidden is what the height animation needs, but it
                      // would also clip the toolbar buttons' invisible touch-target
                      // extensions (TOOLBAR_HIT reaches 4 px sideways and 6 px up and
                      // down). The negative margins here and the matching padding on
                      // the row inside move that reach INSIDE the clipping box while
                      // the row lands on exactly the same pixels as before.
                      className="-mx-1 -my-1.5 min-w-0 flex-1 overflow-hidden"
                      exit={{ opacity: 0, height: 0 }}
                      initial={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                      <section className="flex items-center justify-between gap-2 px-1 py-1.5">
                        <section className="flex items-center gap-1">
                          {!disableFileSelection && (
                            <button
                              aria-label={t("questioninput.upload_file")}
                              title={attachTitle}
                              type="button"
                              className={cn(
                                buttonVariants({ size: "md", variant: "ghost" }),
                                "text-muted-foreground hover:text-foreground dark:hover:text-accent-foreground size-7 cursor-pointer px-0",
                                TOOLBAR_HIT
                              )}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setFilePickerSource(defaultFilePickerSource ?? "recent")
                                setFilePickerOpen(true)
                              }}
                            >
                              <Plus className="size-4" />
                            </button>
                          )}
                          {showHighReasoning && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Toggle
                                  data-testid="max-think-toggle"
                                  pressed={enableHighReasoning}
                                  className={cn(
                                    buttonVariants({ size: "md", variant: "ghost" }),
                                    "text-muted-foreground hover:text-foreground dark:hover:text-accent-foreground size-7 px-0 [&_svg]:size-4",
                                    TOOLBAR_HIT,
                                    enableHighReasoning &&
                                      "border-foreground bg-secondary text-secondary-foreground border"
                                  )}
                                  onPressedChange={onHighReasoningChange}
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                  }}
                                >
                                  <Orbit />
                                </Toggle>
                              </TooltipTrigger>
                              <TooltipContent
                                className="max-w-[250px]"
                                side="bottom"
                                sideOffset={5}
                              >
                                <p>{t("questioninput.high_reasoning")}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {children}
                          {showReasoningMode && reasoningMode && onReasoningModeChange && (
                            <>
                              <ReasoningModePill
                                availableModels={availableReasoningModels}
                                disabled={showCancelOption}
                                effortOverride={effortOverride}
                                mode={reasoningMode}
                                modelOverride={modelOverride}
                                selectedModel={selectedReasoningModel}
                                showShortcutHints={modelShortcutsEnabled}
                                onEffortOverrideChange={onEffortOverrideChange}
                                onModeChange={onReasoningModeChange}
                                onModelOverrideChange={onModelOverrideChange}
                              />
                              {/* TODO: re-enable ResponseTimingBadge once timing accuracy is verified */}
                            </>
                          )}
                          {isVoiceModeEnabled && !hideVoiceMode && (
                            <VoiceInput
                              disabled={showCancelOption}
                              hideButton={!!hideVoiceInputButton}
                              sessionId={currentSessionId ?? undefined}
                              sessionType={sessionType}
                              onAssistantTranscript={handleVoiceAssistantTranscript}
                              onAssistantTranscriptDelta={handleVoiceAssistantTranscriptDelta}
                              onBeforeVoiceModeStart={onBeforeVoiceModeStart}
                              onError={handleVoiceError}
                              onFairUseRefusal={handleVoiceFairUseRefusal}
                              onGetSendSessionContext={onGetSendSessionContext}
                              onInterrupt={handleVoiceInterrupt}
                              onSessionCreated={handleVoiceSessionCreated}
                              onToolComplete={handleVoiceToolComplete}
                              onToolStart={handleVoiceToolStart}
                              onTranscriptionFailure={handleVoiceTranscriptionFailure}
                              onUserTranscript={handleVoiceUserTranscript}
                              onVoiceModeEnd={handleVoiceModeEnd}
                              onGetToggleVoiceMode={(fn) => {
                                toggleVoiceModeRef.current = fn
                              }}
                            />
                          )}
                        </section>
                        <section className="flex items-center gap-2">
                          <ProjectEmbeddingIndicator
                            className="text-xs"
                            projectId={projectId}
                            onClick={() => setSettingsOpen(true)}
                          />
                          {actionButtons}
                        </section>
                      </section>
                    </motion.div>
                  )}
                </AnimatePresence>
                <UsageIndicator className="ml-auto" />
              </section>
            </div>
          </section>

          <p className="text-muted-foreground self-center text-xs">
            {t("questioninput.ai_generated_content_warning")}
            {getInjectedLinks().map((link) => (
              <span key={link.id}>
                {" "}
                <a
                  className="text-primary hover:text-primary/80 underline"
                  href={link.url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {link.label}
                </a>
              </span>
            ))}
          </p>
        </section>
      </footer>
      <FilePickerDialog
        accept={filePickerAccept}
        isNavigator={isNavigator}
        localFormatsHint={filePickerInfoMessage}
        open={filePickerOpen}
        source={filePickerSource}
        onDocumentsSelected={(items) => onDocumentsSelected?.(items)}
        onLocalFilesRejected={onFilePickerFilesRejected}
        onLocalFilesSelected={(files) => tryAddFiles(files)}
        onOpenChange={setFilePickerOpen}
        onSelect={(item) => onGraphFileSelected?.(item)}
      />
      {projectId && sessionType && (
        <ProjectSettingsDialog
          defaultSection="files"
          open={settingsOpen}
          project={project}
          projectId={projectId}
          sessionType={sessionType}
          onOpenChange={setSettingsOpen}
        />
      )}
    </>
  )
}
